<template>
  <div style="margin-top:20px">
    <FiPageRail title="Settings History" />

    <!-- filters, then the date search laid out like the actuals toolbar: preset
         chips and a From/To cluster with double arrows shifting by the loaded
         window and single arrows by a day. presets and arrows reload on their
         own; a hand-picked From/To waits for Search so that setting two dates
         fires one query instead of one per keystroke. -->
    <div v-if="items" class="actuals-cm-toolbar settings-history-toolbar">
      <span class="admin-users-count">{{items.length}} of {{items_full.length}}</span>
      <b-form-select size="sm" v-model="tenant_filter" :options="tenantOptions" class="admin-users-select"/>
      <b-form-select size="sm" v-model="tenant_type_filter" :options="tenantTypeOptions" class="admin-users-select"/>
      <b-form-select size="sm" v-model="telematics_type_filter" :options="telematicsTypeOptions" class="admin-users-select"/>
      <span class="filters-divider"></span>
      <div class="actuals-date-presets" role="group" aria-label="Date range">
        <span class="actuals-range-label">Range</span>
        <button
          v-for="opt in quick_range_options"
          :key="'qr-' + opt.value"
          type="button"
          :class="['actuals-date-chip', {'actuals-date-chip--active': active_preset === opt.value}]"
          :aria-pressed="active_preset === opt.value ? 'true' : 'false'"
          :title="opt.title"
          v-on:click.prevent="set_quick_range(opt.value)"
        >{{ opt.label }}</button>
      </div>
      <div class="actuals-filter-dates actuals-filter-dates--open">
        <span class="actuals-filter-dateunit">
          <b-button-group class="toolbar-date-shifts">
            <b-button size="sm" variant="outline-secondary" class="toolbar-date-shift-btn" title="Shift back one window" v-on:click.prevent="nudge_range_days(-day_count)"><i class="fa fa-angle-double-left" aria-hidden="true"></i></b-button>
            <b-button size="sm" variant="outline-secondary" class="toolbar-date-shift-btn" title="Previous day" v-on:click.prevent="nudge_range_days(-1)"><i class="fa fa-angle-left" aria-hidden="true"></i></b-button>
          </b-button-group>
          <span class="actuals-filter-field">
            <span class="actuals-range-label">From</span>
            <b-form-input v-model="from_date_iso" type="date" size="sm" class="actuals-filter-date" :max="today_iso"/>
          </span>
        </span>
        <span class="actuals-filter-dateunit">
          <span class="actuals-filter-field">
            <span class="actuals-range-label">To</span>
            <b-form-input v-model="to_date_iso" type="date" size="sm" class="actuals-filter-date" :max="today_iso"/>
          </span>
          <b-button-group class="toolbar-date-shifts">
            <b-button size="sm" variant="outline-secondary" class="toolbar-date-shift-btn" title="Next day" :disabled="!can_shift_forward" v-on:click.prevent="nudge_range_days(1)"><i class="fa fa-angle-right" aria-hidden="true"></i></b-button>
            <b-button size="sm" variant="outline-secondary" class="toolbar-date-shift-btn" title="Shift forward one window" :disabled="!can_shift_forward" v-on:click.prevent="nudge_range_days(day_count)"><i class="fa fa-angle-double-right" aria-hidden="true"></i></b-button>
          </b-button-group>
        </span>
      </div>
      <b-button
        size="sm"
        :variant="range_pending ? 'primary' : 'secondary'"
        :title="range_pending ? 'Search the picked date range -- the results below are still the previous range' : 'Search'"
        v-on:click.prevent="load_data()"
      >Search</b-button>
    </div>

    <div class="settings-history-chips-row">
      <template v-if="topTenants.length">
        <span class="chip-group-label">Tenants:</span>
        <button
          v-for="t in topTenants" :key="'tenant-' + t.value"
          type="button"
          class="chip"
          :class="{ 'chip-active': tenant_filter === t.value }"
          :title="t.label + ' (' + t.count + ')'"
          v-on:click.prevent="toggle_tenant_chip(t.value)"
        >{{ t.label }} ({{ t.count }})</button>
      </template>
      <template v-if="topUsers.length">
        <span class="chip-group-label">Users:</span>
        <button
          v-for="u in topUsers" :key="'user-' + u.value"
          type="button"
          class="chip"
          :class="{ 'chip-active': updated_by_filter === u.value }"
          :title="u.label + ' (' + u.count + ')'"
          v-on:click.prevent="toggle_user_chip(u.value)"
        >{{ u.label }} ({{ u.count }})</button>
      </template>
      <template v-if="topFields.length">
        <span class="chip-group-label">Fields:</span>
        <button
          v-for="f in topFields" :key="'field-' + f.value"
          type="button"
          class="chip"
          :class="{ 'chip-active': field_filter === f.value }"
          :title="f.label + ' (' + f.count + ')'"
          v-on:click.prevent="toggle_field_chip(f.value)"
        >{{ f.label }} ({{ f.count }})</button>
      </template>
    </div>

    <div style="margin:20px">
      <b-table :busy="loading" sticky-header="70vh" bordered :items="items" :fields="fields"
               :current-page="currentPage" :per-page="perPage">

        <template #table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner class="align-middle"></b-spinner>
          </div>
        </template>

        <template slot="tenant_name" slot-scope="row">
          {{ row.item.tenant_name }}
        </template>

        <template slot="processed_time" slot-scope="row">
          {{row.item.processed_time | formatDateTimeSmallYear(row.item.timezone)}}
        </template>

        <template slot="updated_by" slot-scope="row">
          <div>{{ format_updated_by(row.item.updated_by) }}</div>
          <div v-if="display_name_differs(row.item.updated_by)" class="updated-by-username">
            {{ row.item.updated_by }}
          </div>
        </template>

        <template slot="diff" slot-scope="row">
          <settings-history-diff-cell :item="row.item" />
        </template>

      </b-table>
      <b-row>
        <b-col>
          <b-pagination
              v-model="currentPage"
              :total-rows="items.length"
              :per-page="perPage"
              align="center"
          >
          </b-pagination>
        </b-col>
      </b-row>
    </div>

  </div>
</template>

<script>

import {get_all_tenant_config_history, get_all_users} from '../networking/apis'
import {mapState} from "vuex";
import moment from 'moment'
import settings_history_diff_cell from './settings_history_diff_cell.vue'
import {get_changes, field_root, humanize_path} from '@/helpers/settings_history_diff'

const TOP_N = 3

export default {
  name: 'admin_settings_history',
  components: {
    'settings-history-diff-cell': settings_history_diff_cell,
  },

  data() {
    return {
      items: [],
      items_full: [],
      loading: true,
      tenant_filter: '',
      tenant_type_filter: '',
      telematics_type_filter: '',
      updated_by_filter: '',
      field_filter: '',
      // the window the table is showing, as a from|to|mode key. a hand-picked date
      // only moves from_date / to_date, so this lags them until Search is pressed
      loaded_key: '',
      // the 1h chip is the one preset a day-granular From/To cannot express, so it
      // rides as a flag that any other range action clears
      hour_window: false,
      from_date: moment().subtract(6, 'days').format('YYYY-MM-DD'),
      to_date: moment().format('YYYY-MM-DD'),
      quick_range_options: [
        { value: 'hour', label: '1h', title: 'Last hour' },
        { value: 1, label: '1d', title: 'Today' },
        { value: 7, label: '7d', title: 'Last 7 days' },
        { value: 30, label: '30d', title: 'Last 30 days' },
      ],
      users_by_username: {},
      currentPage: 1,
      perPage: 200,
      fields: [
        { key: 'tenant_name', label: 'Tenant', sortable: true, thStyle: { width: "160px" }},
        { key: 'processed_time', label: 'When', sortable: true, thStyle: { width: "150px" }},
        { key: 'updated_by', label: 'Updated by', sortable: true, thStyle: { width: "180px" }},
        { key: 'diff', label: 'Changes', sortable: false},
      ],
    }
  },

  async created() {
    await this.load_data()
  },
  methods: {
    range_bounds() {
      if (this.hour_window)
        return { since: moment().subtract(1, 'hours').toISOString(), until: null }
      return {
        since: moment(this.from_date, 'YYYY-MM-DD').startOf('day').toISOString(),
        until: moment(this.to_date, 'YYYY-MM-DD').endOf('day').toISOString(),
      }
    },
    set_quick_range(value) {
      const today = moment().format('YYYY-MM-DD')
      this.hour_window = value === 'hour'
      this.from_date = this.hour_window ? today : moment().subtract(value - 1, 'days').format('YYYY-MM-DD')
      this.to_date = today
      this.load_data()
    },
    // the double arrows pass the loaded window, the single arrows a day. forward
    // shifts stop at today rather than sliding the window into an empty future
    nudge_range_days(days) {
      const room = moment(this.today_iso, 'YYYY-MM-DD').diff(moment(this.to_date, 'YYYY-MM-DD'), 'days')
      const shift = days > 0 ? Math.min(days, room) : days
      if (!shift)
        return
      this.hour_window = false
      this.from_date = moment(this.from_date, 'YYYY-MM-DD').add(shift, 'days').format('YYYY-MM-DD')
      this.to_date = moment(this.to_date, 'YYYY-MM-DD').add(shift, 'days').format('YYYY-MM-DD')
      this.load_data()
    },
    async load_data() {
      this.loading = true
      try {
        const range_key = this.range_key
        const { since, until } = this.range_bounds()
        const history_promise = get_all_tenant_config_history(since, until)
        const users_promise = get_all_users().catch(() => null)

        const [history_resp, users_resp] = await Promise.all([history_promise, users_promise])
        this.users_by_username = this.build_users_map(users_resp)

        const {value: { data = {} } } = history_resp
        const records = data.records || []
        this.items_full = records.map(record => this.normalize_history_record(record))
        this.loaded_key = range_key
        this.filter_results()
      } catch(e) {
        this.$emit('showAlert', 'error retrieving data, try logging in again or contact support if needed', 'danger', 5000)
      }
      this.loading = false
    },
    normalize_history_record(record) {
      if (!record) return record
      const normalized = { ...record }
      if (normalized.diff != null && typeof normalized.diff !== 'string') {
        try {
          normalized.diff = JSON.stringify(normalized.diff)
        } catch (e) {
          normalized.diff = String(normalized.diff)
        }
      }
      if (normalized.htmldiff != null && typeof normalized.htmldiff !== 'string') {
        try {
          normalized.htmldiff = JSON.stringify(normalized.htmldiff)
        } catch (e) {
          normalized.htmldiff = String(normalized.htmldiff)
        }
      }
      // precompute once for the top-fields chips and the field_filter — get_changes() parses
      // jsondiff/old-new snapshots, no need to redo that work on every filter/render pass
      normalized._changes = get_changes(normalized)
      return normalized
    },
    build_users_map(users_resp) {
      const map = {}
      try {
        const results = users_resp && users_resp.value && users_resp.value.data
          ? (users_resp.value.data.results || [])
          : []
        for (const u of results) {
          if (u && u.username) {
            map[u.username.toLowerCase()] = u
          }
        }
      } catch (e) {
        // best-effort lookup; fall back to username if anything goes wrong
      }
      return map
    },
    format_updated_by(username) {
      if (!username) return ''
      const user = this.users_by_username[String(username).toLowerCase()]
      if (!user) return username
      const first = (user.first_name || '').trim()
      const last = (user.last_name || '').trim()
      const full = [first, last].filter(Boolean).join(' ')
      return full || username
    },
    display_name_differs(username) {
      if (!username) return false
      const formatted = this.format_updated_by(username)
      return formatted && formatted !== username
    },
    filter_results() {
      this.items = this.items_full
      if (this.tenant_filter != '')
        this.items = this.items.filter(item => item.tenant_id === this.tenant_filter)
      if (this.tenant_type_filter != '')
        this.items = this.items.filter(item => item.tenant_type === this.tenant_type_filter)
      if (this.telematics_type_filter != '')
        this.items = this.items.filter(item => item.telematics_type === this.telematics_type_filter)
      if (this.updated_by_filter != '')
        this.items = this.items.filter(item => item.updated_by === this.updated_by_filter)
      if (this.field_filter != '')
        this.items = this.items.filter(item => (item._changes || []).some(c => field_root(c.path) === this.field_filter))
    },
    top_n_counts(items, keyFn, labelFn) {
      const counts = {}
      const labels = {}
      for (const item of items) {
        const key = keyFn(item)
        if (!key) continue
        counts[key] = (counts[key] || 0) + 1
        if (!labels[key]) labels[key] = labelFn(item)
      }
      return Object.entries(counts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, TOP_N)
        .map(([key, count]) => ({ value: key, label: labels[key], count }))
    },
    toggle_tenant_chip(value) {
      this.tenant_filter = this.tenant_filter === value ? '' : value
    },
    toggle_user_chip(value) {
      this.updated_by_filter = this.updated_by_filter === value ? '' : value
    },
    toggle_field_chip(value) {
      this.field_filter = this.field_filter === value ? '' : value
    },
  },
  computed:{
    ...mapState({
      user: state => state.profile.user
    }),
    today_iso() {
      return moment().format('YYYY-MM-DD')
    },
    // native date inputs write here; a hand-picked date is never the 1h window,
    // so the setters drop that flag. writes that change nothing are ignored so a
    // v-model echo of a programmatic value cannot clear it either
    from_date_iso: {
      get() { return this.from_date },
      set(v) {
        if (!v || v === this.from_date) return
        this.from_date = v
        this.hour_window = false
      },
    },
    to_date_iso: {
      get() { return this.to_date },
      set(v) {
        if (!v || v === this.to_date) return
        this.to_date = v
        this.hour_window = false
      },
    },
    // the loaded window in calendar days, inclusive: 7d is seven days, so the
    // double arrows step to the adjacent window with no overlap
    day_count() {
      return Math.max(1, moment(this.to_date, 'YYYY-MM-DD').diff(moment(this.from_date, 'YYYY-MM-DD'), 'days') + 1)
    },
    // a preset is "active" when the window happens to match it, so a hand-picked
    // range lights nothing rather than needing a mode to sit in
    active_preset() {
      if (this.hour_window) return 'hour'
      if (this.to_date !== this.today_iso) return null
      const match = this.quick_range_options.find(opt => opt.value === this.day_count)
      return match ? match.value : null
    },
    range_key() {
      return this.from_date + '|' + this.to_date + '|' + (this.hour_window ? 'h' : 'd')
    },
    // a hand-picked date leaves the table on the previous window, so the Search
    // button carries the fact that the two no longer agree
    range_pending() {
      return !!this.loaded_key && this.range_key !== this.loaded_key
    },
    can_shift_forward() {
      return this.to_date < this.today_iso
    },
    tenantOptions() {
      const options = (this.user.tenant_options || []).slice().sort((a, b) => a.text.localeCompare(b.text))
      return [{ value: '', text: 'All Tenants' }, ...options]
    },
    tenantTypeOptions() {
      const types = this.user.types || {}
      const unique = [...new Set(Object.values(types).filter(Boolean))].sort()
      return [{ value: '', text: 'All Tenant Types' }, ...unique.map(t => ({ value: t, text: t }))]
    },
    telematicsTypeOptions() {
      const types = this.user.telematics_types || {}
      const unique = [...new Set(Object.values(types).filter(Boolean))].sort()
      return [{ value: '', text: 'All Telematics Types' }, ...unique.map(t => ({ value: t, text: t }))]
    },
    // top-N chips reflect the full loaded date-range window (items_full), not the
    // currently-filtered `items` — so they stay meaningful reference points rather than
    // collapsing to trivial single entries once you've already filtered by that facet
    topTenants() {
      return this.top_n_counts(this.items_full, item => item.tenant_id, item => item.tenant_name)
    },
    topUsers() {
      return this.top_n_counts(this.items_full, item => item.updated_by, item => this.format_updated_by(item.updated_by))
    },
    topFields() {
      const counts = {}
      for (const item of this.items_full) {
        const roots = new Set((item._changes || []).map(c => field_root(c.path)))
        for (const root of roots) {
          counts[root] = (counts[root] || 0) + 1
        }
      }
      return Object.entries(counts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, TOP_N)
        .map(([root, count]) => ({ value: root, label: humanize_path(root), count }))
    },
  },
  watch: {
    // ISO dates order as strings, so the from-after-to clamp needs no parsing
    from_date(v) {
      if (v > this.to_date) this.to_date = v
    },
    to_date(v) {
      if (v < this.from_date) this.from_date = v
    },
    tenant_filter() {
      this.filter_results()
    },
    tenant_type_filter() {
      this.filter_results()
    },
    telematics_type_filter() {
      this.filter_results()
    },
    updated_by_filter() {
      this.filter_results()
    },
    field_filter() {
      this.filter_results()
    },
  }
}
</script>

<style scoped>

  /* the actuals toolbar classes are inline-flex and take their alignment from
     an ancestor this page does not have, so the row's own layout is set here */
  .settings-history-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 8px 10px;
    margin: 15px 10px 10px;
  }

  .settings-history-toolbar .toolbar-date-shifts {
    flex-shrink: 0;
  }

  .settings-history-toolbar .toolbar-date-shift-btn {
    padding: 0.15rem 0.35rem;
    line-height: 1;
    min-width: 1.65rem;
  }

  .filters-divider {
    width: 1px;
    align-self: stretch;
    background: #d0d8e4;
    margin: 0 2px;
  }

  .admin-users-count {
    font-size: 14px;
    font-weight: 600;
    color: #555;
  }

  .admin-users-select {
    width: auto;
    min-width: 150px;
  }

  .updated-by-username {
    font-size: 11px;
    color: #888;
    margin-top: 2px;
    word-break: break-all;
  }

  .settings-history-chips-row {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: center;
    gap: 6px;
    margin: 0 10px 15px;
    overflow-x: auto;
    padding-bottom: 4px;
  }

  .chip-group-label {
    font-size: 12px;
    font-weight: 600;
    color: #666;
    margin-right: 2px;
    margin-left: 10px;
    flex-shrink: 0;
  }

  .chip-group-label:first-child {
    margin-left: 0;
  }

  .chip {
    border: 1px solid steelblue;
    background: rgba(70, 130, 180, 0.08);
    color: steelblue;
    border-radius: 12px;
    padding: 2px 8px;
    font-size: 11px;
    cursor: pointer;
    transition: background 0.12s, border-color 0.12s;
    max-width: 170px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .chip:hover {
    background: rgba(70, 130, 180, 0.18);
  }

  .chip-active {
    background: steelblue;
    border-color: steelblue;
    color: white;
  }

</style>
